import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

@Component({
  selector: 'app-barcode-laser',
  templateUrl: './barcode-laser.component.html',
  styleUrls: ['./barcode-laser.component.scss']
})

export class BarcodeLaserComponent {
  private scanBuffer = '';
  private lastKeyTime = 0;
  private scanTimeout: NodeJS.Timeout | null = null;
  private readonly SCAN_TIMEOUT_MS = 150;
  private readonly MIN_BARCODE_LENGTH = 4;
  private readonly LASER_SCAN_SPEED_THRESHOLD = 60; // ms between characters for laser detection
  private readonly CONSISTENCY_THRESHOLD = 30; // ms variance for consistent timing
  private characterTimings: number[] = []; // Track timing between characters

  @Input() public isActive = false;
  @Output() public readonly scanSuccess = new EventEmitter<string>();
  @Output() public readonly enterKeyPressed = new EventEmitter<void>();
  @Output() public readonly escapeKeyPressed = new EventEmitter<void>();

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (!this.isActive) {
      return;
    }

    if (event.key === 'Enter') {
      this.enterKeyPressed.emit();
      this.finalizeLaserScan();
      return;
    }

    if (event.key === 'Escape') {
      this.escapeKeyPressed.emit();
      this.resetLaserScanBuffer();
      return;
    }

    if (!this.isValidBarcodeCharacter(event.key)) {
      return;
    }

    const currentTime = Date.now();
    const timeDiff = currentTime - this.lastKeyTime;

    // Track character timing for laser scanner detection
    if (this.lastKeyTime > 0) {
      this.characterTimings.push(timeDiff);
    }

    if (timeDiff < this.SCAN_TIMEOUT_MS) {
      this.scanBuffer += event.key;
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }
      // Only set timeout for laser scanner input
      if (this.isLaserScannerInput()) {
        this.scanTimeout = setTimeout(() => {
          this.finalizeLaserScan();
        }, this.SCAN_TIMEOUT_MS);
      }
    } else {
      this.scanBuffer = event.key;
      this.characterTimings = []; // Reset timings for new sequence
    }

    this.lastKeyTime = currentTime;
  }

  public resetScanBuffer(): void {
    this.resetLaserScanBuffer();
  }

  private finalizeLaserScan(): void {
    if (this.scanBuffer.length >= this.MIN_BARCODE_LENGTH) {
      this.scanSuccess.emit(this.scanBuffer.trim());
    }
    this.resetLaserScanBuffer();
  }

  private resetLaserScanBuffer(): void {
    this.scanBuffer = '';
    this.lastKeyTime = 0;
    this.characterTimings = [];

    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
      this.scanTimeout = null;
    }
  }

  private isLaserScannerInput(): boolean {
    // Laser scanners typically input characters very quickly (under 50ms between characters)
    // and have consistent timing patterns
    if (this.characterTimings.length < 2) {
      return false; // Need at least 2 timings to determine pattern
    }

    const averageTime = this.characterTimings.reduce((sum, time) => sum + time, 0) / this.characterTimings.length;
    const fastInputs = this.characterTimings.filter((time) => time <= this.LASER_SCAN_SPEED_THRESHOLD).length;
    const consistentTiming = this.characterTimings.every((time) => Math.abs(time - averageTime) < this.CONSISTENCY_THRESHOLD);

    // Consider it laser input if:
    // 1. Average time between characters is very fast (< 60ms)
    // 2. Most inputs are fast (> 70% under threshold)
    // 3. Timing is relatively consistent (within 30ms variance)
    return averageTime < this.LASER_SCAN_SPEED_THRESHOLD &&
           (fastInputs / this.characterTimings.length) > 0.7 &&
           consistentTiming;
  }

  private isValidBarcodeCharacter(key: string): boolean {
    return /^[a-zA-Z0-9\-_.*+/\\]$/.test(key);
  }
}
